import { useEffect, useMemo, useState } from "react";
import brandLogo from "../../../assets/images/tnp-logo-neon.png";
import titleChallengesIcon from "../../../assets/images/hub-icons/cropped/title-challenges.png";
import AppLayout from "../../../shared/components/AppLayout";
import SearchInput from "../../../shared/components/SearchInput";
import Toast from "../../../shared/components/Toast";
import UserAvatar from "../../../shared/components/UserAvatar";
import { normalizeSearchText } from "../../../shared/utils/searchText";
import {
  getPublicOrganizerVisualIdentity,
  OrganizerDirectoryItem,
  listOrganizers,
} from "../../auth/shared/services/authService";
import { listPublicChallenges } from "../services/challengeAdminService";
import { ChallengeHistoryItem } from "../types/challengeAdmin";
import "../../../shared/styles/sourceHub.css";

type ShowcaseKind = "challenges" | "tournaments";
type ShowcaseOrganizer = OrganizerDirectoryItem & {
  eventsCount: number;
  finishedEventsCount: number;
  searchTerms: string[];
};

type ShowcaseStats = {
  events: number;
  participants: number;
  finished: number;
};

export function filterShowcaseOrganizers(
  organizers: ShowcaseOrganizer[],
  search: string,
) {
  const query = normalizeSearchText(search);
  return query
    ? organizers.filter((organizer) =>
        [organizer.name, ...(organizer.searchTerms || [])].some((term) =>
          normalizeSearchText(term).includes(query),
        ),
      )
    : organizers;
}

export function shouldSkipOrganizerShowcase({
  loading,
  error,
  organizersCount,
}: {
  loading: boolean;
  error: string;
  organizersCount: number;
}) {
  return !loading && !error && organizersCount === 0;
}

const COPY = {
  challenges: {
    title: "Desafios",
    subtitle: "Escolha os desafios oficiais do TNP ou de outros organizadores.",
    officialTitle: "Desafios Oficiais TNP",
    officialDescription: "Visualize os desafios oficiais publicados pelo circuito TNP.",
    eventSingular: "desafio",
    eventPlural: "desafios",
    actionLabel: "Ver Desafios",
    officialHref: "#/desafios/tnp",
    organizerHref: (id: number) => `#/desafios/organizador/${id}`,
  },
  tournaments: {
    title: "Torneios",
    subtitle: "Escolha os torneios oficiais do TNP ou de outros organizadores.",
    officialTitle: "Torneios Oficiais TNP",
    officialDescription: "Visualize os torneios oficiais publicados pelo circuito TNP.",
    eventSingular: "torneio",
    eventPlural: "torneios",
    actionLabel: "Ver Torneios",
    officialHref: "#/torneios/tnp",
    organizerHref: (id: number) => `#/torneios/organizador/${id}`,
  },
} as const;

async function loadAllOrganizers() {
  const items: OrganizerDirectoryItem[] = [];
  let offset = 0;
  for (;;) {
    const response = await listOrganizers({ limit: 50, offset });
    items.push(...(response.items || []));
    if (response.nextOffset === null) break;
    offset = response.nextOffset;
  }
  return items;
}

function eventOrganizerId(event: ChallengeHistoryItem) {
  const id = Number(event.organizerUserId || 0);
  return Number.isFinite(id) && id > 0 ? id : null;
}

function countParticipants(events: ChallengeHistoryItem[]) {
  const cpfs = new Set<string>();
  events.forEach((event) => {
    (event.participantCpfs || []).forEach((cpf) => {
      const normalized = String(cpf || "").replace(/\D/g, "");
      if (normalized) cpfs.add(normalized);
    });
  });
  if (cpfs.size > 0) return cpfs.size;
  return events.reduce(
    (total, event) => total + Math.max(0, Number(event.participantsCount || 0)),
    0,
  );
}

function OrganizerStats({
  stats,
  eventLabel,
  participantLabel,
}: {
  stats: ShowcaseStats;
  eventLabel: string;
  participantLabel: string;
}) {
  return (
    <div className="hub-landing-stats">
      <div className="hub-landing-stat">
        <span className="hub-landing-stat-icon material-symbols-outlined" aria-hidden="true">
          event
        </span>
        <strong>{stats.events}</strong>
        <small>{eventLabel}</small>
      </div>
      <div className="hub-landing-stat">
        <span className="hub-landing-stat-icon material-symbols-outlined" aria-hidden="true">
          group
        </span>
        <strong>{stats.participants}</strong>
        <small>{participantLabel}</small>
      </div>
      <div className="hub-landing-stat">
        <span className="hub-landing-stat-icon material-symbols-outlined" aria-hidden="true">
          task_alt
        </span>
        <strong>{stats.finished}</strong>
        <small>concluídos</small>
      </div>
    </div>
  );
}

export default function OrganizerShowcase({
  kind,
}: {
  kind: ShowcaseKind;
}) {
  const copy = COPY[kind];
  const [organizers, setOrganizers] = useState<ShowcaseOrganizer[]>([]);
  const [officialStats, setOfficialStats] = useState<ShowcaseStats>({
    events: 0,
    participants: 0,
    finished: 0,
  });
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [errorToastVisible, setErrorToastVisible] = useState(false);

  useEffect(() => {
    let active = true;
    const types =
      kind === "tournaments"
        ? (["tournament"] as const)
        : (["challenge", "super8", "poco"] as const);
    Promise.all([
      loadAllOrganizers(),
      listPublicChallenges({ official: false, types: [...types] }),
      listPublicChallenges({ official: true, types: [...types] }),
    ])
      .then(async ([directory, organizerEvents, officialEvents]) => {
        if (!active) return;
        const counts = new Map<number, number>();
        const finishedCounts = new Map<number, number>();
        const eventLogos = new Map<number, string>();
        const eventSearchTerms = new Map<number, string[]>();
        organizerEvents.forEach((event) => {
          const id = eventOrganizerId(event);
          if (!id) return;
          counts.set(id, (counts.get(id) || 0) + 1);
          if (event.status === "finished") {
            finishedCounts.set(id, (finishedCounts.get(id) || 0) + 1);
          }
          const logo = String(event.organizerLogoUrl || "").trim();
          if (logo && !eventLogos.has(id)) eventLogos.set(id, logo);
          const eventName = String(event.challengeName || "").trim();
          if (eventName) {
            eventSearchTerms.set(id, [
              ...(eventSearchTerms.get(id) || []),
              eventName,
            ]);
          }
        });
        const visibleDirectory = directory.filter(
          (organizer) => (counts.get(Number(organizer.id)) || 0) > 0,
        );
        const identities = await Promise.all(
          visibleDirectory.map(async (organizer) => {
            try {
              const identity = await getPublicOrganizerVisualIdentity(
                Number(organizer.id),
              );
              return [Number(organizer.id), identity] as const;
            } catch (_identityError) {
              return [Number(organizer.id), null] as const;
            }
          }),
        );
        if (!active) return;
        const identityByOrganizerId = new Map(identities);
        setOrganizers(
          visibleDirectory
            .map((organizer) => {
              const identity = identityByOrganizerId.get(Number(organizer.id));
              return {
                ...organizer,
                logoUrl:
                  String(identity?.logoUrl || "").trim() ||
                  String(organizer.logoUrl || "").trim() ||
                  eventLogos.get(Number(organizer.id)) ||
                  null,
                visualIdentityMode:
                  identity?.mode ||
                  organizer.visualIdentityMode ||
                  "tnp_standard",
                primaryColor:
                  identity?.colors?.primaryColor ||
                  organizer.primaryColor ||
                  null,
                secondaryColor:
                  identity?.colors?.secondaryColor ||
                  organizer.secondaryColor ||
                  null,
                accentColor:
                  identity?.colors?.accentColor ||
                  organizer.accentColor ||
                  null,
                backgroundMode:
                  identity?.backgroundMode || organizer.backgroundMode || "blue",
                eventsCount: counts.get(Number(organizer.id)) || 0,
                finishedEventsCount:
                  finishedCounts.get(Number(organizer.id)) || 0,
                searchTerms: eventSearchTerms.get(Number(organizer.id)) || [],
              };
            })
            .sort((a, b) => a.name.localeCompare(b.name, "pt-BR")),
        );
        setOfficialStats({
          events: officialEvents.length,
          participants: countParticipants(officialEvents),
          finished: officialEvents.filter((event) => event.status === "finished")
            .length,
        });
        setError("");
        setErrorToastVisible(false);
      })
      .catch((loadError) => {
        if (!active) return;
        setOrganizers([]);
        setOfficialStats({ events: 0, participants: 0, finished: 0 });
        const message =
          loadError instanceof Error
            ? loadError.message
            : "Não foi possível carregar os organizadores.";
        setError(message);
        setErrorToastVisible(true);
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [kind]);

  const filteredOrganizers = useMemo(() => {
    return filterShowcaseOrganizers(organizers, search);
  }, [organizers, search]);

  useEffect(() => {
    if (
      shouldSkipOrganizerShowcase({
        loading,
        error,
        organizersCount: organizers.length,
      })
    ) {
      window.location.replace(copy.officialHref);
    }
  }, [copy.officialHref, error, loading, organizers.length]);

  const countLabel = (count: number) =>
    `${count} ${count === 1 ? copy.eventSingular : copy.eventPlural}`;

  return (
    <AppLayout title="" subtitle="">
      {error && errorToastVisible ? (
        <Toast
          message={error}
          variant="error"
          onClose={() => setErrorToastVisible(false)}
        />
      ) : null}
      <main className="hub-landing organizer-showcase">
        <div className="hub-landing-lines" aria-hidden="true" />
        <header className="hub-landing-header">
          <div className="hub-landing-icon hub-landing-icon--image" aria-hidden="true">
            <img
              src={titleChallengesIcon}
              alt=""
              className="hub-landing-icon-art hub-landing-icon-art--challenges"
            />
          </div>
          <h1>{copy.title}</h1>
          <p>{copy.subtitle}</p>
        </header>

        <section className="organizer-showcase-directory">
          <div className="organizer-showcase-toolbar">
            <SearchInput
              id={`${kind}-organizer-search`}
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              onClear={() => setSearch("")}
              inputType="search"
              placeholder={`Buscar organizador ou ${copy.eventSingular}`}
              aria-label={`Buscar organizador ou ${copy.eventSingular}`}
            />
          </div>

          {search ? (
            <p className="organizer-showcase-search-result" role="status" aria-live="polite">
              {filteredOrganizers.length}{" "}
              {filteredOrganizers.length === 1
                ? "organizador encontrado"
                : "organizadores encontrados"}
            </p>
          ) : null}

          <div className="hub-landing-grid organizer-showcase-grid">
            <section className="hub-landing-card hub-landing-card--official">
              <div className="hub-landing-card-logo-wrap">
                <img
                  src={brandLogo}
                  alt="TNP"
                  className="hub-landing-card-logo"
                />
              </div>
              <h2>{copy.officialTitle}</h2>
              <p>
                {copy.officialDescription}
              </p>
              <OrganizerStats
                stats={officialStats}
                eventLabel={copy.eventPlural}
                participantLabel="inscritos"
              />
              <a
                className="hub-landing-cta hub-landing-cta--pink hub-landing-cta--choice"
                href={copy.officialHref}
              >
                <span className="material-symbols-outlined" aria-hidden="true">
                  arrow_forward
                </span>
                {copy.actionLabel}
              </a>
            </section>

            {loading ? (
              Array.from({ length: 3 }).map((_, index) => (
                <span
                  className="hub-landing-card organizer-showcase-card--skeleton"
                  key={`organizer-showcase-skeleton-${index}`}
                />
              ))
            ) : filteredOrganizers.length ? (
              filteredOrganizers.map((organizer) => {
                const logoUrl = String(
                  organizer.logoUrl || organizer.avatarUrl || "",
                ).trim();
                return (
                  <section
                    className="hub-landing-card hub-landing-card--organizers"
                    key={`${kind}-organizer-${organizer.id}`}
                  >
                    <div className="hub-landing-card-logo-wrap organizer-showcase-logo">
                      {logoUrl ? (
                        <img
                          src={logoUrl}
                          alt=""
                          className="hub-landing-card-logo hub-landing-card-logo--organizer"
                        />
                      ) : (
                        <UserAvatar
                          name={organizer.name}
                          sex={organizer.sex || ""}
                          fallback="initials"
                          size={72}
                        />
                      )}
                    </div>
                    <h2>{organizer.name}</h2>
                    <p>{countLabel(organizer.eventsCount)} disponíveis.</p>
                    <OrganizerStats
                      stats={{
                        events: organizer.eventsCount,
                        participants: Math.max(
                          0,
                          Number(organizer.athletesCount || 0),
                        ),
                        finished: organizer.finishedEventsCount,
                      }}
                      eventLabel={copy.eventPlural}
                      participantLabel="atletas"
                    />
                    <a
                      className="hub-landing-cta hub-landing-cta--blue hub-landing-cta--choice"
                      href={copy.organizerHref(Number(organizer.id))}
                    >
                      <span className="material-symbols-outlined" aria-hidden="true">
                        arrow_forward
                      </span>
                      {copy.actionLabel}
                    </a>
                  </section>
                );
              })
            ) : null}
          </div>

          {!loading && !error && !filteredOrganizers.length ? (
            <p className="source-hub-empty" role="status">
              Nenhum organizador encontrado.
            </p>
          ) : null}
        </section>
      </main>
    </AppLayout>
  );
}
